import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import type { 
  Location, 
  LocationCluster, 
  LocationCreate, 
  LocationUpdate,
  LocationClusterCreate,
  LocationClusterUpdate
} from '../types/vrp';

// Re-export types that other modules might need
export type { Location, LocationCluster, LocationCreate } from '../types/vrp';

// Hook to get locations for a project with scenario context
export const useLocations = (projectId: number, scenarioId?: number, options?: {
  search?: string;
  page?: number;
  limit?: number;
  dataset_name?: string;
  dataset_version?: number;
  dataset_id?: number;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ['locations', projectId, scenarioId, options],
    enabled: (options?.enabled !== false && projectId > 0) && !!projectId,
    queryFn: async () => {
      try {
        const result = await apiClient.getLocations(projectId, { 
          ...options, 
          scenario_id: scenarioId,
          dataset_name: options?.dataset_name,
          dataset_version: options?.dataset_version,
          dataset_id: options?.dataset_id
        });
        console.log('🔍 [useLocations] Raw API response:', result);
        
        // Extract data from the response structure
        if (result && result.success && Array.isArray(result.data)) {
          console.log('✅ [useLocations] Returning locations array:', result.data.length);
          return result.data;
        }
        
        // Legacy fallback - if result is already an array
        if (Array.isArray(result)) {
          console.log('📦 [useLocations] Legacy array format:', result.length);
          return result;
        }
        
        console.warn('⚠️ [useLocations] Unexpected response format, returning empty array');
        return [];
      } catch (error) {
        console.error('❌ [useLocations] Error fetching locations:', error);
        // Return empty array on error to prevent crashes
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in React Query v5)
  });
};

// Hook to get a single location
export const useLocation = (locationId: number, projectId: number) => {
  return useQuery({
    queryKey: ['location', locationId],
    queryFn: () => apiClient.getLocation(locationId, projectId),
    enabled: !!locationId && !!projectId,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook to create a location
export const useCreateLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (location: LocationCreate) => {
      // Validate required context before API call
      if (!location.scenario_id) {
        throw new Error('scenario_id is required for location creation - ensure scenario context is provided');
      }
      if (!location.dataset_id) {
        throw new Error('dataset_id is required for location creation - ensure dataset context is provided');
      }
      if (!location.project_id) {
        throw new Error('project_id is required for location creation');
      }
      
      console.log('🗺️ Creating location with context:', {
        project_id: location.project_id,
        scenario_id: location.scenario_id,
        dataset_id: location.dataset_id,
        name: location.name
      });
      
      return apiClient.createLocation(location);
    },
    onSuccess: (data, variables) => {
      console.log('🔄 [useCreateLocation] Invalidating queries for project:', variables.project_id);
      
      // Invalidate ALL location-related queries for this project and scenario
      queryClient.invalidateQueries({ queryKey: ['locations', variables.project_id, variables.scenario_id] });
      queryClient.invalidateQueries({ queryKey: ['locations', variables.project_id] });
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      
      // Force refetch of locations
      queryClient.refetchQueries({ queryKey: ['locations', variables.project_id] });
      
      // Add the new location to the cache if we have the ID
      if (data && (data as any).data && (data as any).data.id) {
        queryClient.setQueryData(['location', (data as any).data.id], (data as any).data);
      }
      
      console.log('✅ Location created successfully with context:', {
        locationId: (data as any)?.data?.id || 'unknown',
        scenario_id: variables.scenario_id,
        dataset_id: variables.dataset_id
      });
    },
    onError: (error) => {
      console.error('❌ Location creation failed:', error);
    }
  });
};

// Hook to update a location
export const useUpdateLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, projectId, updates }: { 
      id: number; 
      projectId: number; 
      updates: LocationUpdate 
    }) => apiClient.updateLocation(id, projectId, updates),
    onSuccess: (data, variables) => {
      // Update the location in cache
      queryClient.setQueryData(['location', variables.id], data);
      // Invalidate locations list for the project
      queryClient.invalidateQueries({ queryKey: ['locations', variables.projectId] });
    },
  });
};

// Hook to delete a location
export const useDeleteLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, projectId }: { id: number; projectId: number }) => 
      apiClient.deleteLocation(id, projectId),
    onSuccess: (_, variables) => {
      // Remove location from cache
      queryClient.removeQueries({ queryKey: ['location', variables.id] });
      // Invalidate locations list for the project
      queryClient.invalidateQueries({ queryKey: ['locations', variables.projectId] });
    },
  });
};

// Location Clusters Hooks - COMMENTED OUT (API methods not implemented yet)

// Hook to get location clusters for a project
export const useLocationClusters = (projectId: number) => {
  return useQuery({
    queryKey: ['location-clusters', projectId],
    queryFn: () => Promise.resolve([]), // Placeholder - API not implemented
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in React Query v5)
  });
};

// Hook to get a single location cluster
export const useLocationCluster = (clusterId: number, projectId: number) => {
  return useQuery({
    queryKey: ['location-cluster', clusterId],
    queryFn: () => Promise.resolve(null), // Placeholder - API not implemented
    enabled: !!clusterId && !!projectId,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook to create a location cluster
export const useCreateLocationCluster = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (cluster: LocationClusterCreate) => Promise.resolve({ id: Date.now(), ...cluster }), // Placeholder
    onSuccess: (data, variables) => {
      // Invalidate clusters list for the project
      queryClient.invalidateQueries({ queryKey: ['location-clusters', variables.project_id] });
      // Add the new cluster to the cache
      queryClient.setQueryData(['location-cluster', (data as any).id], data);
    },
  });
};

// Hook to update a location cluster
export const useUpdateLocationCluster = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, projectId, updates }: { 
      id: number; 
      projectId: number; 
      updates: LocationClusterUpdate 
    }) => Promise.resolve({ id, ...updates }), // Placeholder - API not implemented
    onSuccess: (data, variables) => {
      // Update the cluster in cache
      queryClient.setQueryData(['location-cluster', variables.id], data);
      // Invalidate clusters list for the project
      queryClient.invalidateQueries({ queryKey: ['location-clusters', variables.projectId] });
    },
  });
};

// Hook to delete a location cluster
export const useDeleteLocationCluster = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id }: { id: number; projectId: number }) => 
      Promise.resolve({}), // Placeholder - API not implemented
    onSuccess: (_, variables) => {
      // Remove cluster from cache
      queryClient.removeQueries({ queryKey: ['location-cluster', variables.id] });
      // Invalidate clusters list for the project
      queryClient.invalidateQueries({ queryKey: ['location-clusters', variables.projectId] });
      // Invalidate locations that might reference this cluster
      queryClient.invalidateQueries({ queryKey: ['locations', variables.projectId] });
    },
  });
};

// Geocoding utilities (can be expanded to use real geocoding services)
export interface GeocodingResult {
  address: string;
  lat: number;
  lon: number;
  confidence: number;
  components: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    postal_code?: string;
  };
}

export const geocodingService = {
  async geocode(address: string): Promise<GeocodingResult[]> {
    // This is a mock implementation
    // In production, integrate with Google Maps, Mapbox, or similar service
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const mockResults: GeocodingResult[] = [
      {
        address: `${address} (Geocoded)`,
        lat: -26.2041 + (Math.random() - 0.5) * 0.1,
        lon: 28.0473 + (Math.random() - 0.5) * 0.1,
        confidence: 0.95,
        components: {
          street: address.split(',')[0],
          city: 'Johannesburg',
          state: 'Gauteng',
          country: 'South Africa',
          postal_code: '2000',
        },
      },
    ];
    
    return mockResults;
  },

  async reverseGeocode(lat: number, lon: number): Promise<GeocodingResult> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return {
      address: `${lat.toFixed(4)}, ${lon.toFixed(4)} - Geocoded Address`,
      lat,
      lon,
      confidence: 0.9,
      components: {
        street: 'Sample Street',
        city: 'Johannesburg',
        state: 'Gauteng',
        country: 'South Africa',
        postal_code: '2000',
      },
    };
  },
};
